import type { PrioridadTarea, SemanaPublic, TareaPublic } from '@app/shared';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Check } from 'lucide-react';

import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';
import { dentroDeSemana, diaMes, diasDeSemana, hoy, porPrioridad } from '../lib/fechas';

// ciclo-tareas-deadline, R6 (18/10/2026): las tareas activas como un kanban de
// 7 columnas, lunes a domingo, cada una en el día que vence y de la más
// prioritaria a la menos. Con la semana PLANIFICADA, arrastrar a otra columna
// cambia el vencimiento; con la semana GUARDADA solo se tilda, igual que en la
// vista Lista.
const PRIORIDAD_BORDE: Record<PrioridadTarea, string> = {
  ALTA: 'border-l-danger',
  MEDIA: 'border-l-warning',
  BAJA: 'border-l-success',
};

interface Props {
  tareas: TareaPublic[];
  semana: SemanaPublic;
  onCompletar: (tarea: TareaPublic) => void;
  onEditar: (tarea: TareaPublic) => void;
  onMover: (tarea: TareaPublic, venceEl: string) => void;
}

export function VistaSemana({ tareas, semana, onCompletar, onEditar, onMover }: Props): JSX.Element {
  const es = useI18n();
  const t = es.tareas;
  const semanaGuardada = semana.estado === 'GUARDADA';
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const dias = diasDeSemana(semana.inicio);
  const nombres = t.dias.split('|');
  const hoyEs = hoy();

  // Las tareas sin fecha (o con una de otra semana) no aparecen acá: se ven en
  // la vista Lista con su "Sin fecha", y "Guardar planificación" avisa cuáles
  // faltan. Un bloque propio arriba ocupaba demasiado (Javier, 19/10/2026).
  const activas = tareas.filter((x) => x.activa && dentroDeSemana(x.venceEl, semana.inicio, semana.fin));
  const porDia = new Map(dias.map((d) => [d, activas.filter((x) => x.venceEl === d).sort(porPrioridad)]));

  function onDragEnd(e: DragEndEvent): void {
    const tarea = activas.find((x) => x.id === e.active.id);
    const destino = e.over?.id;
    if (!tarea || typeof destino !== 'string' || destino === tarea.venceEl) return;
    onMover(tarea, destino);
  }

  const tarjeta = (tarea: TareaPublic): JSX.Element => (
    <TarjetaDia
      key={tarea.id}
      tarea={tarea}
      semanaGuardada={semanaGuardada}
      onCompletar={() => onCompletar(tarea)}
      onEditar={() => onEditar(tarea)}
    />
  );

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-7 gap-2">
        {dias.map((dia, i) => (
          <ColumnaDia
            key={dia}
            dia={dia}
            titulo={`${nombres[i] ?? ''} ${diaMes(dia)}`}
            esHoy={dia === hoyEs}
            etiquetaHoy={t.vistaSemana.hoy}
            vacio={t.vistaSemana.vacio}
          >
            {(porDia.get(dia) ?? []).map(tarjeta)}
          </ColumnaDia>
        ))}
      </div>
    </DndContext>
  );
}

interface ColumnaProps {
  dia: string;
  titulo: string;
  esHoy: boolean;
  etiquetaHoy: string;
  vacio: string;
  children: JSX.Element[];
}

function ColumnaDia({ dia, titulo, esHoy, etiquetaHoy, vacio, children }: ColumnaProps): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: dia });
  return (
    <section
      ref={setNodeRef}
      data-dia={dia}
      aria-label={titulo}
      className={cn(
        'flex min-h-[max(16rem,calc(100vh-16.5rem))] min-w-0 flex-col gap-2 rounded-xl border bg-surface/70 p-2 transition-colors',
        esHoy ? 'border-marca-primario' : 'border-border-subtle',
        isOver && 'bg-marca-neutro/60',
      )}
    >
      <header className="flex items-baseline justify-between px-1">
        <span className="text-sm font-medium capitalize text-text-strong">{titulo}</span>
        {esHoy && <span className="text-xs font-medium text-marca-primario">{etiquetaHoy}</span>}
      </header>
      {children.length === 0 ? <p className="px-1 text-xs text-text-faint">{vacio}</p> : children}
    </section>
  );
}

interface TarjetaProps {
  tarea: TareaPublic;
  semanaGuardada: boolean;
  onCompletar: () => void;
  onEditar: () => void;
}

function TarjetaDia({ tarea, semanaGuardada, onCompletar, onEditar }: TarjetaProps): JSX.Element {
  const es = useI18n();
  const t = es.tareas;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: tarea.id,
    disabled: semanaGuardada,
  });

  return (
    <div
      ref={setNodeRef}
      data-testid="tarjeta-dia"
      data-prioridad={tarea.prioridad}
      style={{ transform: CSS.Translate.toString(transform) }}
      {...attributes}
      {...listeners}
      aria-label={semanaGuardada ? tarea.titulo : `${tarea.titulo}. ${t.vistaSemana.arrastrar}`}
      className={cn(
        'flex touch-none items-start gap-2 rounded-lg border-l-4 bg-surface p-2 shadow-card transition-shadow hover:shadow-card-hover',
        PRIORIDAD_BORDE[tarea.prioridad],
        !semanaGuardada && 'cursor-grab active:cursor-grabbing',
        isDragging && 'relative z-20 opacity-80 shadow-card-hover',
      )}
    >
      <button
        type="button"
        onClick={onCompletar}
        disabled={!semanaGuardada}
        aria-label={tarea.completada ? t.descompletar : t.completar}
        aria-pressed={tarea.completada}
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40',
          tarea.completada
            ? 'border-marca-primario bg-marca-primario text-white'
            : 'border-marca-neutro text-transparent hover:border-marca-primario',
        )}
      >
        <Check size={12} />
      </button>
      <button
        type="button"
        onClick={onEditar}
        disabled={semanaGuardada}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <span
          className={cn(
            'line-clamp-3 text-sm font-medium leading-snug text-text-strong',
            tarea.completada && 'text-text-faint line-through',
          )}
        >
          {tarea.titulo}
        </span>
      </button>
    </div>
  );
}
